<template>
  <div class="k2-barwrap">
    <div class="k2-bar" role="banner">
      <button type="button" class="k2-ib" data-fk="back" :aria-label="tx('back')" @click="$emit('back')">
        <k2-icon n="mdi-arrow-left" :s="22" />
      </button>
      <span v-if="showLogo" class="k2-logo" aria-hidden="true"><k2-icon n="mdi-silverware-fork-knife" :s="19" /></span>
      <div class="k2-tt">
        <b>{{ $t("kitchen_display") }}</b>
        <span>{{ subtitle }}</span>
      </div>

      <template v-if="!narrow">
        <div class="k2-mid">
          <div class="k2-seg" role="group" :aria-label="tx('type.label')">
            <button
              v-for="p in pills"
              :key="p.v"
              type="button"
              class="k2-pill"
              :class="{ 'k2-pill--ic': !p.lab }"
              :data-fk="'type-' + p.v"
              :aria-pressed="p.on ? 'true' : 'false'"
              :aria-label="p.name + ', ' + p.n"
              @click="$emit('type', p.v)"
            >
              <k2-icon :n="p.icon" :s="18" />
              <span v-if="p.lab">{{ p.short }}</span>
              <i class="n">{{ p.n }}</i>
            </button>
          </div>
          <button
            v-if="isAdmin"
            type="button"
            class="k2-chip"
            :class="{ 'k2-chip--on': restOn }"
            data-fk="rest"
            aria-haspopup="listbox"
            :aria-expanded="popKind === 'rest' ? 'true' : 'false'"
            @click="$emit('rest', $event.currentTarget)"
          >
            <k2-icon n="mdi-storefront-outline" :s="18" /><span>{{ restLabel }}</span><k2-icon n="mdi-chevron-down" :s="16" />
          </button>
          <label v-if="searchOpen" class="k2-q">
            <k2-icon n="mdi-magnify" :s="18" />
            <input
              data-fk="q"
              type="search"
              inputmode="search"
              autocomplete="off"
              :placeholder="tx('search.ph')"
              :aria-label="tx('search.label')"
              :value="q"
              @input="$emit('q-input', $event.target.value)"
            />
            <button type="button" class="k2-qx" data-fk="qx" :aria-label="tx('search.clear')" @click.prevent="$emit('q-clear')">
              <k2-icon n="mdi-close" :s="16" />
            </button>
          </label>
          <button v-else type="button" class="k2-ib" data-fk="qb" :aria-label="tx('search.open')" :title="tx('search.title')" @click="$emit('q-open')">
            <k2-icon n="mdi-magnify" :s="22" />
          </button>
          <button
            v-if="lateCount || onlyLate"
            type="button"
            class="k2-late"
            data-fk="late"
            :aria-pressed="onlyLate ? 'true' : 'false'"
            :aria-label="onlyLate ? tx('late.all') : tx('late.only')"
            @click="$emit('late')"
          >
            <k2-icon n="mdi-alert-outline" :s="18" /><span>{{ tx("late.label") }}</span><i class="n">{{ lateCount }}</i>
          </button>
        </div>
        <div class="k2-right">
          <span class="k2-st" :class="'k2-st--' + conn.cls" role="status" :aria-label="conn.text">
            <i></i><span v-if="wc !== 'md'">{{ conn.text }}</span>
          </span>
          <button
            type="button"
            class="k2-ib"
            data-fk="snd"
            :aria-pressed="sound ? 'true' : 'false'"
            :aria-label="sound ? tx('sound.on') : tx('sound.off')"
            :title="tx('sound.title')"
            @click="$emit('sound')"
          >
            <k2-icon :n="sound ? 'mdi-volume-high' : 'mdi-volume-off'" :s="22" />
          </button>
          <button
            v-if="wc === 'xl'"
            type="button"
            class="k2-chip"
            :class="{ 'k2-chip--on': !live }"
            data-fk="date"
            aria-haspopup="dialog"
            :aria-expanded="popKind === 'date' ? 'true' : 'false'"
            @click="$emit('date', $event.currentTarget)"
          >
            <k2-icon :n="live ? 'mdi-history' : 'mdi-calendar-blank-outline'" :s="18" /><span>{{ dateLabel }}</span><k2-icon n="mdi-chevron-down" :s="16" />
          </button>
          <div class="k2-clk">{{ clock }}<small>{{ dateText }}</small></div>
          <button type="button" class="k2-ib" data-fk="set" aria-haspopup="dialog" :aria-label="tx('set.title')" :title="tx('set.title')" @click="$emit('settings')">
            <k2-icon n="mdi-tune-variant" :s="22" />
          </button>
          <button
            v-if="wc === 'xl' && fullscreenSupported"
            type="button"
            class="k2-ib"
            data-fk="fs"
            :aria-label="fullscreen ? tx('fullscreen.exit') : tx('fullscreen.enter')"
            :title="tx('fullscreen.enter')"
            @click="$emit('fullscreen')"
          >
            <k2-icon :n="fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'" :s="22" />
          </button>
        </div>
      </template>

      <template v-else>
        <span class="k2-sp"></span>
        <span class="k2-st" :class="'k2-st--' + conn.cls" role="status" :aria-label="conn.text"><i></i></span>
        <button v-if="!searchOpen" type="button" class="k2-ib" data-fk="qb" :aria-label="tx('search.open')" :title="tx('search.title')" @click="$emit('q-open')">
          <k2-icon n="mdi-magnify" :s="22" />
        </button>
        <button
          v-if="!phone"
          type="button"
          class="k2-ib"
          data-fk="snd"
          :aria-pressed="sound ? 'true' : 'false'"
          :aria-label="sound ? tx('sound.on') : tx('sound.off')"
          :title="tx('sound.title')"
          @click="$emit('sound')"
        >
          <k2-icon :n="sound ? 'mdi-volume-high' : 'mdi-volume-off'" :s="22" />
        </button>
        <button type="button" class="k2-ib" data-fk="set" aria-haspopup="dialog" :aria-label="tx('set.title')" :title="tx('set.title')" @click="$emit('settings')">
          <k2-icon n="mdi-tune-variant" :s="22" />
        </button>
      </template>
    </div>

    <div v-if="narrow" class="k2-sub">
      <button
        v-if="isAdmin && !phone"
        type="button"
        class="k2-chip"
        :class="{ 'k2-chip--on': restOn }"
        data-fk="rest"
        aria-haspopup="listbox"
        :aria-expanded="popKind === 'rest' ? 'true' : 'false'"
        @click="$emit('rest', $event.currentTarget)"
      >
        <k2-icon n="mdi-storefront-outline" :s="18" /><span>{{ restLabel }}</span><k2-icon n="mdi-chevron-down" :s="16" />
      </button>
      <div class="k2-seg" role="group" :aria-label="tx('type.label')">
        <button
          v-for="p in pills"
          :key="p.v"
          type="button"
          class="k2-pill"
          :class="{ 'k2-pill--ic': !p.lab }"
          :data-fk="'type-' + p.v"
          :aria-pressed="p.on ? 'true' : 'false'"
          :aria-label="p.name + ', ' + p.n"
          @click="$emit('type', p.v)"
        >
          <k2-icon :n="p.icon" :s="18" />
          <span v-if="p.lab">{{ p.short }}</span>
          <i class="n">{{ p.n }}</i>
        </button>
      </div>
      <button
        v-if="lateCount || onlyLate"
        type="button"
        class="k2-late"
        data-fk="late"
        :aria-pressed="onlyLate ? 'true' : 'false'"
        :aria-label="onlyLate ? tx('late.all') : tx('late.only')"
        @click="$emit('late')"
      >
        <k2-icon n="mdi-alert-outline" :s="18" /><span v-if="!phone">{{ tx("late.label") }}</span><i class="n">{{ lateCount }}</i>
      </button>
      <label v-if="searchOpen" class="k2-q">
        <k2-icon n="mdi-magnify" :s="18" />
        <input
          data-fk="q"
          type="search"
          inputmode="search"
          autocomplete="off"
          :placeholder="tx('search.ph')"
          :aria-label="tx('search.label')"
          :value="q"
          @input="$emit('q-input', $event.target.value)"
        />
        <button type="button" class="k2-qx" data-fk="qx" :aria-label="tx('search.clear')" @click.prevent="$emit('q-clear')">
          <k2-icon n="mdi-close" :s="16" />
        </button>
      </label>
    </div>
  </div>
</template>

<script>
import K2Icon from "./K2Icon.vue";
import kds2Text from "@/mixins/kds2Text";
import { TYPES } from "@/utils/kdsTickets";

// Gornja traka (jedan red umjesto tri): vrsta narudžbe sa brojem, restoran (administrator), pretraga, "Kasni",
// veza, zvuk, sat, podešavanja. Ispod 1000 px traka se dijeli na dva reda.
export default {
  name: "Kds2Bar",
  components: { K2Icon },
  mixins: [kds2Text],
  props: {
    wc: { type: String, required: true },
    phone: { type: Boolean, default: false },
    counts: { type: Object, required: true },
    type: { type: [String, Number], default: "all" },
    lateCount: { type: Number, default: 0 },
    onlyLate: { type: Boolean, default: false },
    conn: { type: Object, required: true },
    sound: { type: Boolean, default: false },
    q: { type: String, default: "" },
    qOpen: { type: Boolean, default: false },
    live: { type: Boolean, default: true },
    dateLabel: { type: String, default: "" },
    clock: { type: String, default: "" },
    dateText: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    isAdmin: { type: Boolean, default: false },
    restLabel: { type: String, default: "" },
    restOn: { type: Boolean, default: false },
    popKind: { type: String, default: "" },
    fullscreen: { type: Boolean, default: false },
    fullscreenSupported: { type: Boolean, default: false },
  },
  computed: {
    narrow() {
      return this.wc === "nr";
    },
    showLogo() {
      return this.wc !== "md" && !this.phone;
    },
    searchOpen() {
      return this.qOpen || !!this.q;
    },
    // oznaka se vidi na TV-u uvijek, a na užim ekranima samo na izabranoj vrsti
    pills() {
      const wide = this.wc === "xl";
      const onlyActive = this.wc === "lg" || (this.narrow && !this.phone);
      const items = [{ v: "all", icon: "mdi-view-grid-outline", short: this.$t("all"), name: this.$t("all") }].concat(
        TYPES.map((meta) => ({ v: meta.type, icon: meta.icon, short: this.tx(meta.shortKey), name: this.$t(meta.labelKey) }))
      );
      return items.map((item) => {
        const on = this.type === item.v;
        return { ...item, on, n: this.counts[item.v] || 0, lab: wide || (onlyActive && on) };
      });
    },
  },
  methods: {
    focusSearch() {
      this.$nextTick(() => {
        const input = this.$el.querySelector("input[type=search]");
        if (input) input.focus();
      });
    },
  },
};
</script>
